/**
 * Phase 5 PR 5.1 — `useWeeklyPlan` hook.
 *
 * Returns the current week's plan in the shape `WeekPlan` /
 * `WeekPlanDesktop` already consumes (the `WeeklyPlan` type from
 * `fixtures/plan.ts`).
 *
 * "Current week" determination (Season-model 2b — single resolver):
 *
 *   1. Find the user's active season → `useActiveSeason` (caller does this).
 *   2. The caller passes the resolved current `weekNumber` (from
 *      `resolveCurrentWeek` via `phaseFromMacroPlan`). NO `MAX(week_number)`
 *      fallback — null weekNumber → empty (pre-season / generate empty-state).
 *   3. Fetch that exact `(season_id, week_number)` row (newest on duplicates).
 *   4. Embed plan_days (the table is keyed by plan_id).
 *   5. Adapt to `WeeklyPlan` shape using `currentWeekDates` for
 *      `dayNumber` (calendar day-of-month for the displayed week).
 *
 * `today` flag is set on the row whose `day_of_week` equals
 * `todayMondayBasedLocal()` (Monday-based, see `weekdayUtils.ts`).
 *
 * Returns `null` (not a fixture) when there's no active season, no
 * training_plans, or the query fails. The dashboard renders an
 * empty-state in those cases per Decision 4 of the Phase 5 plan
 * ("never show another athlete's fixture data as a placeholder").
 *
 * B55 — a SECOND, independent read: when `hk_import_v1` is on, the hook also
 * pulls `GET /api/runs` so a workout imported from Apple Health credits the
 * plan day it started on, exactly as an in-app run's "add to today's session"
 * does (the importer writes only `runs`, never `plan_days` — the founder's
 * ring workout showed in Runs but left the objective unfilled). The matching
 * rule lives in `lib/actualForPlanDay.creditForPlanDay`; the read is fail-soft
 * and never blocks or breaks the week.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import type { Day, WeeklyPlan } from '../fixtures/plan';
import { DAY_SHORT_UPPER, currentWeekDates } from '../lib/weekdayUtils';
import { isB19ActualsEnabled } from '../lib/featureFlag';
import {
  B19_TOLERANCE_FLOOR_MI,
  B19_TOLERANCE_PCT,
  actualForPlanDay,
  creditForPlanDay,
  fromMiles,
  heroActualSubline,
  toMiles,
  type DistanceUnit,
  type PlanCreditRun,
} from '../lib/actualForPlanDay';
import { useAuthFetch } from './useAuthFetch';
import { useServerFlag } from './useServerFlag';
import { onImportSaved } from '../lib/health/workoutImport';
import { planTextInUnit } from '../components/today/sessionInUnit';
import { readDayExpanded, type DayExpanded } from '../components/WeekPlan/dayDetailsDisplay';

export interface UseWeeklyPlanArgs {
  /** Active season id (typically from `useActiveSeason`). When null, the
   *  hook returns null without querying. */
  seasonId: string | null;
  /**
   * The macro plan's CURRENT week_number (calendar-derived, from the
   * `resolveCurrentWeek` resolver via `phaseFromMacroPlan`). The hook returns
   * THAT week's plan (newest row if legacy duplicates exist), so the day-rows
   * match the hero footer's "Week X of Y" (also resolver-derived).
   *
   * Season-model 2b: when null/undefined (resolver yielded
   * before_season/no_plan/invalid), the hook returns null — there is NO
   * `MAX(week_number)` fallback. The caller renders the pre-season / generate
   * empty-state rather than a stale arbitrary week.
   */
  weekNumber?: number | null;
  /**
   * SP-1 (F2) — the CURRENT macro week's `week_start_date` (from the same
   * `season_macro_plan` row the hero footer resolves). When provided, the card's
   * 7 cells span THIS date + 0..6 so Today/plan/calendar agree on one week
   * model; for a Monday-anchored (new) season this is Mon..Sun. Null/omitted →
   * the calendar week containing today (prior behavior; also the legacy path).
   */
  weekStartDate?: string | null;
  /**
   * B-19 — the athlete's display unit (`athlete_context.distance_unit`). Used
   * only when the `b19_actuals_v1` flag is ON, to render stored actuals in the
   * athlete's unit. Default 'mi'.
   */
  displayUnit?: DistanceUnit;
}

/**
 * B332 / B314 / B330 — what the Today reward strip needs beyond `WeeklyPlan`.
 * `WeeklyPlan.weekMileageDone` / `weekMileageTarget` stay miles for every athlete
 * (the contract the desktop list and the coach rules read); these speak the
 * athlete's own unit, computed from the UNROUNDED miles so a km athlete's 7 km run
 * reads 7, not the 6 that rounding to whole miles first would give.
 */
export interface WeekDistanceSummary {
  /** The unit the two figures below are stated in (the athlete's display unit). */
  distanceUnit: DistanceUnit;
  /** Distance done this week, whole numbers, in `distanceUnit`. */
  weekDistanceDone: number;
  /** Distance planned this week, whole numbers, in `distanceUnit`. */
  weekDistanceTarget: number;
  /**
   * B330 — true only when something is done AND the distance done reaches the plan
   * for the days already passed, plus today once today's session is done. The
   * reward strip prints "on track" only on true; there is no "behind" message.
   */
  weekOnTrack: boolean;
}

export type WeeklyPlanWithDistance = WeeklyPlan & WeekDistanceSummary;

export interface UseWeeklyPlanResult {
  data: WeeklyPlanWithDistance | null;
  /**
   * Phase 6c.1 — `training_plans.id` for the current-week row. Needed
   * by `usePlanDayCompletion` for the PUT endpoint
   * (`PUT /api/plans/:planId/days/:dayId`). Null while loading or
   * when no plan exists yet.
   */
  planId: string | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

interface PlanDayRow {
  id: string;
  day_of_week: number; // Monday-based 0..6
  day_label: string;
  workout_type: string;
  details: string | null;
  expanded_details: Record<string, string> | null;
  /** Phase 6c.1 — completion logging. 'completed' | 'skipped' |
   *  'upcoming' | null (default treated as 'upcoming'). */
  completion_status?: 'completed' | 'skipped' | 'upcoming' | null;
  /** RPE restore — perceived effort 1-10. Already in the `plan_days(*)`
   *  select; was simply unmapped since the React migration. */
  rpe_value?: number | null;
  /** B-19 — the stored actual (`plan_days.actual_distance` / unit). Already in
   *  the `plan_days(*)` select; unmapped until B-19. Paired value+unit or both
   *  null (the AD-2.1 integrity invariant). */
  actual_distance?: number | null;
  actual_distance_unit?: DistanceUnit | null;
}

interface TrainingPlanRow {
  id: string;
  user_id: string;
  season_id: string;
  week_number: number;
  title: string;
  weekly_total: string | null;
  intensity: string | null;
  status: string;
  created_at: string;
  plan_days: PlanDayRow[] | null;
}

/**
 * Format the week range as "Apr 20 to 26" using `Intl.DateTimeFormat`
 * for the user's locale. Mirrors the existing fixture's `dateRange`.
 *
 * The joiner is the word "to", never an en dash: the athlete-facing copy
 * register carries no en or em dashes (B87 leftovers, 2026-09-08).
 *
 * The month is printed ONCE when both dates share it ("Apr 20 to 26") and
 * twice only when they differ ("Apr 27 to May 3"); the year is never shown.
 * Repeating it cost the Today kicker its single line at 375 to 393: "THIS
 * WEEK · SEP 7 TO SEP 13" needed 176.9px in a header row that also carries
 * "Open week ›" and "Details", so it wrapped, orphaned "13", and moved the
 * whole page below the card down 14px (UX round 4, D-A, 2026-09-09). It is
 * the rule `lib/formatWeekRange.ts` (the chat save banner) already applies,
 * so Today, the banner and the SeasonGlance strip read alike.
 */
function formatDateRange(monday: Date, sunday: Date): string {
  const fmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
  const end =
    monday.getMonth() === sunday.getMonth() ? String(sunday.getDate()) : fmt.format(sunday);
  return `${fmt.format(monday)} to ${end}`;
}

/**
 * Best-effort numeric extraction from a free-form mileage string.
 * Returns 0 when no leading number is found (e.g. "—", "Rest").
 *
 * The mileage column on plan_days is free-text — Llama 3.3 emits things
 * like "5 mi at 8:30/mi", "3 mi", "—", "rest". For the WeekPlan grid we
 * just want the number so the daily mileage display is consistent. The
 * full string still goes into `Day.mileage` for the cell text.
 */
function extractMiles(text: string | null | undefined): number {
  if (!text) return 0;
  const m = text.match(/(\d+(?:\.\d+)?)/);
  if (!m) return 0;
  return Number(m[1]) || 0;
}

/** Miles per unit for distance-token normalization (deriveDayMetric). */
const MILES_PER_MI = 1;
const MILES_PER_KM = 0.621371;
const MILES_PER_M = 1 / 1609.344;

/**
 * One distance token = an optional rep multiplier ("6 ×"), a number (with an
 * optional range whose FIRST value we keep), and a unit. The trailing
 * `(?![a-z])` rejects the shared "mi" prefix inside "min"/"minutes" and the "m"
 * inside "metering", and lets "meters"/"metres" through as METERS not miles.
 * Case-insensitive + global so `deriveDayMetric` can SUM every token in a day.
 */
const DISTANCE_TOKEN_RE =
  /(?:(\d+)\s*[×xX]\s*)?(\d+(?:\.\d+)?)(?:\s*[-–]\s*\d+(?:\.\d+)?)?\s*(miles?|mi|kilometers?|kilometres?|km|meters?|metres?|m)(?![a-z])/gi;

/**
 * B416 — the figure the match starts on is the tail of a TIME ("00" in "6:00 km",
 * "30" in "8:30 mi"): a pace written with its unit, not a distance. The token
 * pattern alone cannot tell, because it finds "00 km" inside "6:00 km", which made
 * a km athlete's pace-only day read "0 km" ("7.0 of 0 km" under a logged run) and a
 * mile athlete's "8:30 mi" read 30 miles. Only a colon right after a digit counts
 * (a lookbehind would not parse on the older Safari the web app still serves), so
 * "Warm-up:2 mi" is still a distance.
 */
function isTimeTail(text: string, index: number): boolean {
  return /\d:$/.test(text.slice(Math.max(0, index - 2), index));
}

/** B416 — a whole pace with its unit ("6:00 km", "1:06:00 km"), for the bare-number fallback below. */
const PACE_WITH_UNIT_RE =
  /\d+:\d+(?::\d+)?(?:\.\d+)?\s*(?:miles?|mi|kilometers?|kilometres?|km|meters?|metres?|m)(?![a-z])/gi;

/** Normalize a matched unit string to a miles-per-unit factor. */
function milesPerUnit(unitRaw: string): number {
  const u = unitRaw.toLowerCase();
  if (u.startsWith('mi')) return MILES_PER_MI; // mi / mile / miles
  if (u.startsWith('k')) return MILES_PER_KM; // km / kilometer(s) / kilometre(s)
  return MILES_PER_M; // m / meter(s) / metre(s)
}

/**
 * Sum EVERY distance token in a free-text workout string, in miles, unit- and
 * rep-aware. "6 × 800m" = 6 × 800 m ≈ 2.98 mi of reps; "2 mi warm-up … 2 mi
 * cool-down" adds the warm-up + cool-down. Returns the total, the token count,
 * and — for a lone plain-mile token with no multiplier — the raw first number
 * so single-distance days keep their exact legacy display.
 */
function sumDistanceMiles(
  text: string,
  unit?: DistanceUnit,
): { miles: number; count: number; singleMileRaw: string | null } {
  let miles = 0;
  let count = 0;
  let singleMileRaw: string | null = null;
  // B338 fix 1 (L4) — the end and unit of the previous mile or km token, so a
  // distance restated in the other unit in brackets counts once. B34 units fix 3
  // adds what that token contributed and its rep multiplier, so a restatement
  // written in the ATHLETE's unit can take its place.
  let prev: { end: number; isMile: boolean; miles: number; reps: number } | null = null;
  DISTANCE_TOKEN_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = DISTANCE_TOKEN_RE.exec(text)) !== null) {
    // B416: "6:00 km" is a pace, not "00 km".
    if (isTimeTail(text, m.index)) continue;
    const unitLower = m[3].toLowerCase();
    const isMile = unitLower.startsWith('mi');
    const isMetre = !isMile && !unitLower.startsWith('k');
    // "10 km (6.2 mi)" and "6 mi (9.7 km)": the bracketed figure is the SAME
    // distance in the other unit, not a second one. B34 units fix 3: so is the
    // slash form "6 mi/9.7 km" or "5 mi / 8 km", read exactly as `planTextInUnit`
    // reads it (one figure after the slash, no rep multiplier, not followed by a slash).
    if (prev !== null && !isMetre && isMile !== prev.isMile) {
      const between = text.slice(prev.end, m.index);
      const after = text.slice(m.index + m[0].length);
      const bracketed = /^\s*\(\s*$/.test(between) && /^\s*\)/.test(after);
      const slashed = /^\s*\/\s*$/.test(between) && !m[1] && !after.startsWith('/');
      if (bracketed || slashed) {
        // With the athlete's unit known, the figure written in it is the one counted,
        // once, so the cell agrees with the day panel and the hero (`planTextInUnit`).
        // With no unit the first figure stands, as before.
        if (unit !== undefined && isMile === (unit === 'mi')) {
          miles += Number(m[2]) * milesPerUnit(m[3]) * prev.reps - prev.miles;
          singleMileRaw = count === 1 && prev.reps === 1 && !m[1] && isMile ? m[2] : null;
        }
        prev = null;
        continue;
      }
    }
    const reps = m[1] ? Number(m[1]) || 1 : 1;
    const value = Number(m[2]) || 0;
    const unitRaw = m[3];
    const tokenMiles = value * milesPerUnit(unitRaw) * reps;
    prev = isMetre ? null : { end: m.index + m[0].length, isMile, miles: tokenMiles, reps };
    miles += tokenMiles;
    count += 1;
    // Track the raw number of a single, unmultiplied MILE token so we can echo
    // the legacy display byte-for-byte for ordinary single-distance days.
    singleMileRaw = count === 1 && !m[1] && unitRaw.toLowerCase().startsWith('mi') ? m[2] : null;
  }
  return { miles, count, singleMileRaw };
}

/** One decimal, no trailing ".0" ("10", "6.3"). */
function roundOne(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * B309 fix round — a day's planned distance as the details title states it, in
 * the athlete's own unit. `deriveDayMetric` always speaks miles (the week strip
 * sums miles), so a km athlete's title read "6.3 mi" over a body that said
 * "10.2 km". Summed from the UNROUNDED miles, so the title and the plan text
 * agree to the decimal ("10.2 km" both ways, not 10.1 from a double rounding).
 * Null when nothing needs to change: miles (the legacy display, byte for byte),
 * or a day with no distance token (a duration or a bare number).
 */
function plannedDistanceInUnit(text: string, unit: DistanceUnit): string | null {
  if (unit !== 'km') return null;
  const dist = sumDistanceMiles(text, unit);
  if (dist.count === 0) return null;
  return `${roundOne(fromMiles(dist.miles, 'km'))} km`;
}

// B338 fix 1 — `planTextInUnit` (the details panel's plan text in the athlete's
// own unit, B309 fix round) now lives in `components/today/sessionInUnit`, so the
// Today hero session and this day panel share ONE conversion. Re-exported here.
export { planTextInUnit };

/**
 * Derive the per-day metric cell from a free-text workout `details` string.
 *
 * Running distance renders as "N mi" and contributes to the week's mileage.
 * Time-based work — cross-training, or runs the engine authored in minutes —
 * renders as a duration ("20 to 30 min") and contributes ZERO miles. This fixes
 * the bug where a swim described as "Cross-training (swimming, 20-30 minutes)"
 * rendered as "20 mi" and inflated the weekly total.
 *
 * F-112(e): a MIXED-PACE day (intervals/tempo with a warm-up, reps, and a
 * cool-down) has no single "N mi" — the engine sets its distance to null and
 * expresses the structure in the text. The old code grabbed the FIRST "N mi"
 * token, which is the WARM-UP ("2 mi" for a 6×800m day), or fell through to the
 * bare number and showed the REP COUNT as miles ("6x800m" → "6 mi"). Both were
 * wrong. We now SUM every distance token (unit- and rep-aware) so the strip
 * shows a sensible day TOTAL that agrees with the plan table / day detail; when
 * a day carries no distance at all we show its DURATION — a right number of
 * minutes beats a wrong number of miles.
 *
 * Order matters: distance tokens win, then a duration token, then a bare number.
 */
export function deriveDayMetric(details: string | null | undefined, unit?: DistanceUnit): {
  display: string;
  miles: number;
  /** B28 — the day's planned MINUTES when it was authored in time rather than
   *  distance (0 otherwise). Lets the week header state a time total instead of
   *  going silent on an all-minutes week, without any unit guessing. */
  minutes: number;
} {
  const text = details ?? '';
  // Explicit distance: sum every token ("2 mi warm-up + 6×800m + 2 mi CD").
  const dist = sumDistanceMiles(text, unit);
  if (dist.count > 0) {
    // A lone plain-mile token keeps its exact legacy display (single-distance
    // days unchanged: "4-5 mile" → "4 mi", "12.5 miles" → "12.5 mi").
    if (dist.singleMileRaw !== null) {
      return { display: `${dist.singleMileRaw} mi`, miles: Number(dist.singleMileRaw) || 0, minutes: 0 };
    }
    const mi = Math.round(dist.miles * 10) / 10;
    return { display: `${mi} mi`, miles: mi, minutes: 0 };
  }
  // Time-based: "20-30 minutes", "30 min" — a duration, never distance.
  // The INPUT regex still accepts a hyphen or an en dash (the coach's free
  // text carries both); only the DISPLAY is register copy, so a range reads
  // "20 to 30 min" (B87 leftovers, 2026-09-08).
  const min = text.match(/(\d+)\s*(?:[-–]\s*(\d+))?\s*min(?:ute)?s?\b/i);
  if (min) {
    return {
      display: min[2] ? `${min[1]} to ${min[2]} min` : `${min[1]} min`,
      miles: 0,
      // The FIRST value of a range, matching the display's leading number.
      minutes: Number(min[1]) || 0,
    };
  }
  // Bare leading number with no unit — assume miles (legacy behaviour). B416: a pace with its
  // unit ("6:00 km") is not a bare number, or the pace-only day would read "6 mi".
  const bare = text.replace(PACE_WITH_UNIT_RE, ' ').match(/(\d+(?:\.\d+)?)/);
  if (bare) return { display: `${bare[1]} mi`, miles: Number(bare[1]) || 0, minutes: 0 };
  return { display: '?', miles: 0, minutes: 0 };
}

/**
 * Workout-type → label classification for the pill on each day cell.
 * Maps the free-form `plan_days.workout_type` to the short label the
 * existing `Day.label` field expects. Falls back to the workout_type
 * verbatim when no rule matches (truncated).
 */
export function classifyLabel(workoutType: string | null | undefined): {
  label: string;
  isRest: boolean;
  isHard: boolean;
} {
  const wt = (workoutType ?? '').toLowerCase();
  if (!wt || wt.includes('rest') || wt.includes('off')) {
    return { label: 'Rest', isRest: true, isHard: false };
  }
  if (wt.includes('long')) return { label: 'Long', isRest: false, isHard: true };
  if (wt.includes('tempo') || wt.includes('threshold')) {
    return { label: 'Tempo', isRest: false, isHard: true };
  }
  if (wt.includes('interval') || wt.includes('hill') || wt.includes('repeat')) {
    return { label: 'Hard', isRest: false, isHard: true };
  }
  if (wt.includes('easy') || wt.includes('recovery') || wt.includes('shake')) {
    return { label: 'Easy', isRest: false, isHard: false };
  }
  // Fallback — keep it short; raw workout_type may exceed grid cell.
  // (B397: the cut label is the legacy grid cell's. The 2.0 surfaces that print a
  // session name read the full type through `sessionNameForType`, never this.)
  const trimmed = (workoutType ?? '').slice(0, 8);
  return { label: trimmed || 'Run', isRest: false, isHard: false };
}

/**
 * B397 / W1-TODAY-FIX1 (F4) — the ONE full session name for a plan day's own
 * `workout_type`. Every 2.0 surface that prints a session reads it (through
 * `weekMeasure.sessionName`): the week row, the day panel title, the effort sheet's
 * sub-line and Tomorrow, so one day never reads two ways. It follows the hero title
 * builder's (`useTodaySession.buildTitle`) words in its order, so the hero says the
 * same thing, and a type the rules do not name prints as the plan wrote it:
 * "Cross-training" is never cut to "Cross-tr".
 *
 * Returns null for a rest day or no type; the caller names those ("Rest").
 */
export function sessionNameForType(workoutType: string | null | undefined): string | null {
  const type = (workoutType ?? '').trim();
  const wt = type.toLowerCase();
  if (!wt || wt.includes('rest') || wt.includes('off')) return null;
  if (wt.includes('long')) return 'Long run';
  if (wt.includes('tempo')) return 'Tempo run';
  if (wt.includes('interval')) return 'Intervals';
  if (wt.includes('hill')) return 'Hill workout';
  if (wt.includes('easy')) return 'Easy run';
  if (wt.includes('recovery')) return 'Recovery run';
  return type;
}

/**
 * A week day as the adapter builds it: the `Day` plus the plan day's own workout
 * type (set whenever the plan day has one), which `sessionNameForType` names in full.
 * Additive and read by no legacy surface, so a `Day` without it (a fixture, a
 * hand-built day) reads exactly as before.
 */
export type DayWithType = Day & {
  workoutType?: string;
  /**
   * FIX-TRAIN — the engine's single pace for the day, `expanded_details.pace_target` as stored ("5:40/km"), trimmed.
   * Absent when the day stored none. NOT `Day.pace`: Today's desktop week list prints that field for every account,
   * so this is its own field, read by the 2.0 Train cards only (`components/train/dayPace.ts`).
   */
  paceTarget?: string;
  /** FIX-TRAIN — the unit the athlete reads in, so a pace written in the other unit is not printed (B313). */
  paceUnit?: DistanceUnit;
  /** DAY-DETAILS-1019: the stored fields the 2.0 day panel shows. Absent when the day stores none. */
  expanded?: DayExpanded;
};

/**
 * Build 7 local-midnight cell dates starting at an ISO `week_start_date`
 * (weekStart + 0..6). SP-1 (F2): the card then shows the macro week's actual
 * dates. Returns null on a malformed ISO so the caller falls back.
 */
function weekDatesFromIso(iso: string | null | undefined): Date[] | null {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  const start = new Date(y, m - 1, d); // local midnight
  return [0, 1, 2, 3, 4, 5, 6].map((i) => {
    const dt = new Date(start);
    dt.setDate(start.getDate() + i);
    return dt;
  });
}

/** Same local calendar day (Y/M/D) — the SP-1 date-match today-pin. */
function sameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** A local calendar day as a sortable number (20261011), for before/after tests. */
function localDayKey(d: Date): number {
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

/** A stated weekly total that names kilometres ("40 km"), read as such. */
const KM_TOTAL_RE = /\b(?:km|kilomet(?:er|re)s?)\b/i;

/**
 * B338 — the first kilometre figure of a stated weekly total, in miles. A total that
 * names both units ("40 km (25 mi)") is read once, in the athlete's own unit: summing
 * every distance token counted the same week twice (40 km plus 25 mi read as 80 km).
 * A range keeps its first value ("40-45 km" reads 40), as the day reader does.
 */
const KM_TOTAL_TOKEN_RE = /(\d+(?:\.\d+)?)(?:\s*[-–]\s*\d+(?:\.\d+)?)?\s*(?:km|kilomet(?:er|re)s?)\b/i;

function kmTotalMiles(total: string): number {
  const m = total.match(KM_TOTAL_TOKEN_RE);
  return m ? toMiles(Number(m[1]) || 0, 'km') : 0;
}

/**
 * Adapt a TrainingPlanRow to the `WeeklyPlan` shape. Always returns 7
 * days (Mon..Sun), filling in rest-day placeholders for any day the
 * persisted plan didn't include. Caller can check `data === null` to
 * decide between the empty-state and rendering this output.
 *
 * SP-1 (F2): when `weekStartDate` is threaded (the current macro week's date),
 * the 7 cells span THAT date + 0..6 and the today-pin is a DATE match — so
 * Today's card agrees with the plan/calendar, and the pin is correct even for a
 * legacy non-Monday week whose macro window isn't the calendar week. Null →
 * the calendar week containing today (prior behavior).
 */
function adaptToWeeklyPlan(
  plan: TrainingPlanRow,
  now: Date = new Date(),
  weekStartDate: string | null = null,
  opts: { showActuals?: boolean; displayUnit?: DistanceUnit; runs?: PlanCreditRun[] | null } = {},
): WeeklyPlanWithDistance {
  // B-19 — actuals surfacing is OFF by default so this adapter stays byte-
  // identical to pre-B-19 unless the flag threads `showActuals: true`.
  const showActuals = opts.showActuals === true;
  const displayUnit: DistanceUnit = opts.displayUnit === 'km' ? 'km' : 'mi';
  // B55 — the athlete's recent runs, used ONLY to credit a plan day from an
  // Apple-Health-imported workout (see `creditForPlanDay`). Empty/omitted →
  // every branch below is byte-identical to pre-B55.
  const creditRuns = Array.isArray(opts.runs) ? opts.runs : null;
  const weekDates = weekDatesFromIso(weekStartDate) ?? currentWeekDates(now);

  const planDays = plan.plan_days ?? [];
  const byDow = new Map<number, PlanDayRow>();
  for (const pd of planDays) {
    byDow.set(pd.day_of_week, pd);
  }

  let mileageTarget = 0;
  let mileageDone = 0;
  // B330 — the planned miles of the days that count toward "on track": every day
  // before today, plus today once its session is done.
  let mileagePlannedToDate = 0;
  const todayKey = localDayKey(now);
  // B28 — planned MINUTES on the days the engine authored in time. Only used
  // when the week carries no distance at all, so a distance week is untouched.
  let minutesTarget = 0;
  const days: DayWithType[] = [];
  for (let dow = 0; dow < 7; dow++) {
    const pd = byDow.get(dow) ?? null;
    const date = weekDates[dow];
    if (!pd) {
      // Plan doesn't have this weekday — render as rest placeholder so
      // the 7-cell grid stays consistent.
      days.push({
        dayShort: DAY_SHORT_UPPER[dow],
        dayNumber: date.getDate(),
        label: '—',
        mileage: '—',
        rest: true,
        today: sameLocalDay(date, now),
      });
      continue;
    }

    const { label, isRest, isHard } = classifyLabel(pd.workout_type);
    // Surface the day's metric: running distance as "N mi", time-based work
    // (cross-training / minute-authored runs) as a duration ("20 to 30 min").
    // Only real distance counts toward the week's planned/done mileage — so a
    // swim never inflates the total or shows as miles.
    let mileage = '—';
    let actual: Day['actual'] = null;
    // B309 — the logged distance for the details panel (any flag state).
    let actualDistance: Day['actualDistance'] = null;
    // B309 fix round — the planned distance in the athlete's unit, set only when
    // it differs from the miles-always `mileage` (km athletes).
    let mileageInUnit: string | null = null;
    // B55 — miles credited to this day by an Apple-Health-imported workout;
    // null (the overwhelmingly common case) means nothing changed vs pre-B55.
    let importedCreditMiles: number | null = null;
    if (!isRest && pd.details) {
      const metric = deriveDayMetric(pd.details, displayUnit);
      mileage = metric.display;
      mileageInUnit = plannedDistanceInUnit(pd.details, displayUnit);
      // B332 / B314 — the day's cell speaks the athlete's own unit. A km athlete's
      // `mileageInUnit` ("10.2 km") is that string; a unit-less number the legacy
      // reader takes as miles ("Easy 5") is converted from the same miles the week
      // totals use. A mile athlete keeps `metric.display`, byte for byte.
      if (displayUnit === 'km') {
        if (mileageInUnit) {
          mileage = mileageInUnit;
        } else if (metric.miles > 0 && /\smi$/.test(metric.display)) {
          mileage = `${roundOne(fromMiles(metric.miles, 'km'))} km`;
        }
      }
      mileageTarget += metric.miles;
      minutesTarget += metric.minutes;

      // B55 — an imported workout that STARTED on this local day credits the
      // planned session, the same way an in-app run's "add to today's session"
      // does. Returns null miles whenever a stored actual, an explicit skip, or
      // simply no matching import means there is nothing to credit — so the
      // stored-actual path below is untouched. Rest days never get here.
      importedCreditMiles = creditForPlanDay({
        dayDate: date,
        storedActual: pd.actual_distance,
        completionStatus: pd.completion_status,
        runs: creditRuns,
      }).miles;

      // The day's EFFECTIVE actual: the stored accumulator whenever it exists
      // (always authoritative — B-19 §1.1), else the imported credit, in miles.
      const a: number | null | undefined = importedCreditMiles ?? pd.actual_distance;
      const u: DistanceUnit | null | undefined =
        importedCreditMiles != null ? 'mi' : pd.actual_distance_unit;
      const actualMi =
        typeof a === 'number' && Number.isFinite(a) && (u === 'mi' || u === 'km')
          ? toMiles(a, u)
          : null;

      // B309 — what the day's details panel shows as the logged distance. A
      // stored 0 is the authoritative skip marker (B-19 §5), not a run, so it
      // shows nothing.
      if (actualMi != null && actualMi > 0) {
        const shown = Math.round(fromMiles(actualMi, displayUnit) * 10) / 10;
        if (shown > 0) actualDistance = { value: shown, unit: displayUnit };
      }

      if (importedCreditMiles != null) {
        // B55 — a credited day is DONE, and its "done" miles are the MEASURED
        // import, never the plan's number. Deliberately outside the B-19 display
        // flag: this path did not exist before B55, so there is no legacy render
        // to stay byte-identical with, and a measured distance is the only
        // honest figure for the strip.
        mileageDone += importedCreditMiles;
      } else if (pd.completion_status === 'completed') {
        // RATIFIABLE 4 — the reward strip's "done" reflects ACTUAL miles of
        // completed days, not planned (a completed 5-of-4 week reads 5 done, not
        // 4). Falls back to planned miles for a completed day with no stored
        // actual (legacy completions predating actual-capture) so the tally
        // never regresses. Flag OFF → planned miles, byte-identical to before.
        mileageDone += showActuals ? actualMi ?? metric.miles : metric.miles;
      }

      // B330 — a day counts toward "on track" once it is behind us, or today once
      // its session is done. A future day, or a today not yet done, is not owed.
      const dayKey = localDayKey(date);
      const dayIsDone = importedCreditMiles != null || pd.completion_status === 'completed';
      if (dayKey < todayKey || (dayKey === todayKey && dayIsDone)) {
        mileagePlannedToDate += metric.miles;
      }

      // B-19 — the per-day planned-vs-actual pairing for the value cell. Only
      // when the flag is on AND a real actual exists; otherwise `actual` stays
      // null and the surfaces render planned-only (byte-identical).
      if (showActuals) {
        // B396 — a km athlete whose plan states km is paired against the plan's OWN
        // km figure (`mileageInUnit`, the one the week cell prints), not the miles
        // `metric.display` round-trips through ("7 km" -> "4.3 mi" -> "6.9 km").
        // `plannedLabel` keeps a planned text already in the display unit verbatim.
        // Miles athletes, mile-stated plans and minute-only days keep `metric.display`.
        const planStatesKm = displayUnit === 'km' && !!mileageInUnit && /\bk(?:m|ilomet)/i.test(pd.details);
        const pairingInput = {
          plannedText: planStatesKm ? mileageInUnit : metric.display,
          plannedMiles: metric.miles,
          actualValue: a,
          actualUnit: u,
          displayUnit,
        };
        const res = actualForPlanDay(pairingInput);
        if (res.state !== 'no_actual' && res.copy) {
          // B-19 — attach BOTH grammars from the ONE helper: the list `copy`
          // ("X of Y mi") the WeekPlan grid renders, and the Today-hero sub-line
          // ("You ran X mi · planned N", frame §8) the hero reads for the TODAY
          // completed cell. Same flag gate (showActuals = isB19ActualsEnabled)
          // + same helper → the whole B-19 display family flips together.
          actual = {
            copy: res.copy,
            state: res.state,
            hero: heroActualSubline(pairingInput),
          };
        }
      }
    }

    // FIX-TRAIN — `expanded_details.pace_target`, the one pace `weekMaterializer.js` stores for a day. Only a
    // non-empty string counts; the card (`dayPace`) decides whether it states exactly one pace in the athlete's unit.
    const storedPace = (pd.expanded_details as Record<string, unknown> | null | undefined)?.pace_target;
    const paceTarget = typeof storedPace === 'string' && storedPace.trim() ? storedPace.trim() : null;
    const expanded = readDayExpanded(pd.expanded_details, (t) => planTextInUnit(t, displayUnit));

    days.push({
      dayShort: DAY_SHORT_UPPER[dow],
      dayNumber: date.getDate(),
      label,
      // F4 — the plan day's own type, for the full session name (additive).
      ...(pd.workout_type ? { workoutType: pd.workout_type } : {}),
      mileage,
      rest: isRest,
      hard: isHard,
      today: sameLocalDay(date, now),
      // Phase 6c.1 — surface the plan_day UUID + completion_status so
      // WeekPlan can wire a click-to-toggle handler against
      // `PUT /api/plans/:planId/days/:dayId`.
      id: pd.id,
      // B55 — a day credited by an imported workout reads DONE, the same as the
      // day an in-app run credited (`addToToday` writes completionStatus
      // 'completed' alongside the distance). This is a DERIVED completion: the
      // durable `plan_days.completion_status` write belongs to the importer (see
      // the lane report's lib/health flag), and once it ships the day carries a
      // stored actual and never routes through the credit at all.
      completionStatus: importedCreditMiles != null ? 'completed' : pd.completion_status ?? null,
      rpe: pd.rpe_value ?? null,
      // B309 — the day's session text and logged distance, for its details panel.
      // The text carries its distances in the athlete's unit (fix round).
      details: planTextInUnit(pd.details, displayUnit),
      ...(mileageInUnit ? { mileageInUnit } : {}),
      // FIX-TRAIN — the engine's pace for the day and the unit it must be read in (see `DayWithType`).
      ...(paceTarget ? { paceTarget } : {}),
      paceUnit: displayUnit,
      ...(expanded ? { expanded } : {}),
      actualDistance,
      // B-19 — null unless the flag is on and a stored actual exists.
      actual,
    });
  }

  // Prefer the plan's stated weekly total if it parses — but ONLY when it
  // actually denotes distance. Engine-authored weeks state training TIME
  // ("240 min"), and reading that bare number as miles rendered the absurd
  // "240 mi planned" hero (same units family as deriveDayMetric's
  // "30 min"→"30 mi"). Time-stated totals fall back to the summed per-day
  // metric, which already counts time-based work as 0 miles.
  // Phase 6c.1 — weekMileageDone is the sum of completed days' real distance,
  // accumulated in the day loop above (cross-training contributes 0 miles).
  const statedTotal = plan.weekly_total ?? '';
  // B332: a km athlete's stated total that names kilometres ("40 km") is read as
  // kilometres, not as 40 miles that would then print as 64 km. Miles athletes
  // keep the leading-number read, unchanged.
  const llmTotal = /\bmin(?:ute)?s?\b/i.test(statedTotal)
    ? 0
    : displayUnit === 'km' && KM_TOTAL_RE.test(statedTotal)
      ? kmTotalMiles(statedTotal)
      : extractMiles(statedTotal);
  const target = llmTotal || mileageTarget;

  return {
    dateRange: formatDateRange(weekDates[0], weekDates[6]),
    weekMileageDone: Math.round(mileageDone),
    weekMileageTarget: Math.round(target),
    // B332 / B314 — the same two figures in the athlete's own unit, from the
    // unrounded miles. Miles athletes: identical to the two figures above.
    distanceUnit: displayUnit,
    weekDistanceDone: Math.round(fromMiles(mileageDone, displayUnit)),
    weekDistanceTarget: Math.round(fromMiles(target, displayUnit)),
    // B330 — nothing done is never "on track"; otherwise done must reach the plan
    // for the days already passed (plus today once done), within the band the app
    // already uses to call a run a match: max(3 percent of planned, 0.1 mi), inclusive
    // (`classifyActual`'s own test, with the same imported constants).
    weekOnTrack:
      mileageDone > 0 &&
      mileagePlannedToDate - mileageDone <=
        Math.max(mileagePlannedToDate * B19_TOLERANCE_PCT, B19_TOLERANCE_FLOOR_MI),
    // B28 — an engine week authored entirely in TIME used to leave this blank,
    // so the header said nothing at all about a week with five sessions in it.
    // State the time total instead of guessing a distance we do not have.
    // Distance weeks are byte-identical (the minutes branch needs target === 0).
    status:
      target > 0
        ? `${Math.round(fromMiles(target, displayUnit))} ${displayUnit} planned`
        : minutesTarget > 0
          ? `${Math.round(minutesTarget)} min planned`
          : '',
    days,
  };
}

/** B55 — a stable empty array so a no-runs render never churns the adapt memo. */
const NO_CREDIT_RUNS: PlanCreditRun[] = [];

export function useWeeklyPlan({
  seasonId,
  weekNumber,
  weekStartDate = null,
  displayUnit = 'mi',
}: UseWeeklyPlanArgs): UseWeeklyPlanResult {
  const { supabase, user } = useAuthContext();
  const userId = user?.id ?? null;
  const fetchAuthed = useAuthFetch();
  // B55 — the whole credit path is inert unless Apple Health import is enabled
  // for the project. Flag OFF (the default, and fail-closed on an unreachable
  // /api/config) → no extra request and a byte-identical week. `runs` is a
  // server-only table (RLS on, no grants), so `/api/runs` is the only read.
  const hkImportOn = useServerFlag('hk_import_v1');

  // B-19 — store the RAW fetched plan; the adapt (which reads displayUnit + the
  // display flag) happens in a useMemo below. This keeps displayUnit OUT of the
  // fetch dependency array, so a display-only unit change (e.g. athlete context
  // resolving 'mi'→'km' async) re-adapts in place WITHOUT an extra network
  // refetch — flag-off network behavior is byte-identical to pre-B-19.
  const [rawPlan, setRawPlan] = useState<TrainingPlanRow | null>(null);
  const [planId, setPlanId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  // B55 — recent runs, narrowed to the four fields the credit reads. Loaded on
  // its OWN track so the week never waits on it: the plan renders immediately
  // and re-adapts in place if/when the runs land.
  const [creditRuns, setCreditRuns] = useState<PlanCreditRun[]>(NO_CREDIT_RUNS);

  const loadCreditRuns = useCallback(async () => {
    if (!userId || !hkImportOn) {
      setCreditRuns(NO_CREDIT_RUNS);
      return;
    }
    // FAIL-SOFT throughout: any miss (flag race, offline, 4xx, malformed body)
    // leaves the runs empty, which is exactly the pre-B55 week. A crediting
    // read must never be able to break the plan.
    try {
      const res = await fetchAuthed('/api/runs');
      if (!res.ok) {
        setCreditRuns(NO_CREDIT_RUNS);
        return;
      }
      const body = (await res.json().catch(() => null)) as { runs?: unknown } | null;
      const rows = Array.isArray(body?.runs) ? (body!.runs as Record<string, unknown>[]) : [];
      // Keep ONLY the credit fields — the response also carries route polylines
      // and coach reactions this hook has no business holding onto.
      setCreditRuns(
        rows.map((r) => ({
          id: typeof r.id === 'string' ? r.id : '',
          started_at: typeof r.started_at === 'string' ? r.started_at : '',
          distance_m: r.distance_m as number | string | null | undefined,
          source: (r.source as string | null | undefined) ?? null,
          // B257 — the athlete's own opt-out of this run's plan-day credit.
          plan_credit_excluded_at: (r.plan_credit_excluded_at as string | null | undefined) ?? null,
        })),
      );
    } catch {
      setCreditRuns(NO_CREDIT_RUNS);
    }
  }, [userId, hkImportOn, fetchAuthed]);

  const refetchPlan = useCallback(async () => {
    // Season-model 2b — NO MAX(week_number) fallback. Without a resolved
    // current week (the macro resolver yielded before_season/no_plan/invalid →
    // weekNumber null), there is no "this week" to show, so we return empty and
    // let the caller render the pre-season / generate empty-state. Showing the
    // arbitrary highest week_number here was the old divergence bug.
    const hasWeek = typeof weekNumber === 'number' && weekNumber >= 1;
    if (!supabase || !userId || !seasonId || !hasWeek) {
      setRawPlan(null);
      setPlanId(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Fetch exactly the resolved current week. A `created_at DESC` tiebreaker
      // + limit 1 picks the NEWEST row, so any legacy duplicate weeks resolve
      // deterministically.
      const { data: rows, error: queryError } = await supabase
        .from('training_plans')
        .select('*, plan_days(*)')
        .eq('user_id', userId)
        .eq('season_id', seasonId)
        .eq('week_number', weekNumber)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (queryError) throw queryError;
      const plan = (rows as TrainingPlanRow | null) ?? null;
      // Store the raw plan; the memo below adapts it (display-only concern).
      setRawPlan(plan);
      setPlanId(plan ? plan.id : null);
    } catch (err) {
      setRawPlan(null);
      setPlanId(null);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
    // displayUnit + weekStartDate are DISPLAY-only (applied in the adapt memo),
    // deliberately NOT in this fetch dep array — they must not trigger a network
    // refetch.
  }, [supabase, userId, seasonId, weekNumber]);

  useEffect(() => {
    void refetchPlan();
  }, [refetchPlan]);

  // B55 — separate effect so the runs load neither blocks nor re-triggers the
  // plan query. It re-fires on its own when `hk_import_v1` resolves ON after
  // boot (loadCreditRuns's identity changes), which is the normal mount race.
  useEffect(() => {
    void loadCreditRuns();
  }, [loadCreditRuns]);

  // B257 — the refresh-race close. An import can save a run while Today is
  // already mounted (no foreground event fires again in that session), so
  // the credit reload must not wait for the next mount/visibility refetch.
  // The importer signals every subscriber once it has saved at least one
  // workout; this re-runs ONLY the runs read (the plan itself hasn't
  // changed), and the adapt memo below re-derives the day from the new
  // `creditRuns` automatically. Inert when the flag is off (no subscribe).
  useEffect(() => {
    if (!hkImportOn) return;
    return onImportSaved(() => {
      void loadCreditRuns();
    });
  }, [hkImportOn, loadCreditRuns]);

  // The caller-facing refetch (used by the visibility/focus refetch) refreshes
  // BOTH — a workout imported while the app was backgrounded should show on
  // return. The runs half is fired, not awaited: it must never delay the week.
  const refetch = useCallback(async () => {
    void loadCreditRuns();
    await refetchPlan();
  }, [loadCreditRuns, refetchPlan]);

  // B-19 — adapt the raw plan to the WeeklyPlan shape. Re-runs when the raw plan
  // OR a display-only input (weekStartDate / displayUnit / B55 runs) changes —
  // no refetch. The display flag is read here (pure URL/localStorage read); OFF
  // → `showActuals` false → byte-identical to pre-B-19.
  const data = useMemo<WeeklyPlanWithDistance | null>(
    () =>
      rawPlan
        ? adaptToWeeklyPlan(rawPlan, new Date(), weekStartDate, {
            showActuals: isB19ActualsEnabled(),
            displayUnit,
            runs: creditRuns,
          })
        : null,
    [rawPlan, weekStartDate, displayUnit, creditRuns],
  );

  if (!supabase || !userId) {
    return { data: null, planId: null, loading: false, error: null, refetch: async () => {} };
  }

  return { data, planId, loading, error, refetch };
}

/**
 * W1-TODAY-WEEK (build spec 7.7) — true when a day's plan text STATES a distance, for
 * the one week measure (`lib/weekMeasure.ts`). Additive: it reads the token pattern
 * above and changes none of the helpers.
 *
 * A figure counts when it is miles or kilometres and is not part of a rep set. A token
 * with an "N ×" multiplier ("6 x 800m", "2 x 5 km") or in metres is a piece of a
 * session, so a day whose only distances are reps states none: the reps alone are a
 * partial total, and the week prints no partial totals. A day with a warm-up and a
 * cool-down around the reps states its distance ("2 mi warm-up, 6 x 800m, 2 mi
 * cool-down" is the 7 mi `deriveDayMetric` sums), and the measure counts that total,
 * never the reps alone.
 */
export function statesDayDistance(details: string | null | undefined): boolean {
  const text = details ?? '';
  let found = false;
  DISTANCE_TOKEN_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = DISTANCE_TOKEN_RE.exec(text)) !== null) {
    if (isTimeTail(text, m.index)) continue; // B416: a pace is not a stated distance
    const unit = m[3].toLowerCase();
    const isMetre = !unit.startsWith('mi') && !unit.startsWith('k');
    if (!m[1] && !isMetre) {
      found = true;
      break;
    }
  }
  DISTANCE_TOKEN_RE.lastIndex = 0;
  return found;
}

// Exported for unit testing the adapter without a live Supabase client.
export const __testing__ = {
  adaptToWeeklyPlan,
  classifyLabel,
  extractMiles,
  deriveDayMetric,
  planTextInUnit,
  plannedDistanceInUnit,
};
